import React, { useState } from 'react';

import AscendantResult from '../components/AscendantResult';
import History from '../components/History';
import SocialShare from '../components/SocialShare';
import { saveToHistory } from '../utils/localStorage';

import calculateAscendant from './calculate_ascendant.js';
import { COUNTIES } from '../constants/constants';

interface FormData {
    birthdate: string;
    birthtime: string;
    birthlocation: number;
}

function AscendantForm() {
    const [ascendant, setAscendant] = useState('');
    const [ascendantImage, setAscendantImage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [historyRefresh, setHistoryRefresh] = useState(0);
    const [formData, setFormData] = useState<FormData>({
        birthdate: '1992-11-25',
        birthtime: '08:27',
        birthlocation: COUNTIES[0]?.id || 0
    });

    const getImage = (ascendant: string) => {
            switch (ascendant) {
                case 'Aries':
                    return '/images/aries.png';
                case 'Taurus':
                    return '/images/taurus.png';
                case 'Gemini':
                    return '/images/gemini.png';
                case 'Cancer':
                    return '/images/cancer.png';
                case 'Leo':
                    return '/images/leo.png';
                case 'Virgo':
                    return '/images/virgo.png';
                case 'Libra':
                    return '/images/libra.png';
                case 'Scorpio':
                    return '/images/scorpio.png';
                case 'Sagittarius':
                    return '/images/sagittarius.png';
                case 'Capricorn':
                    return '/images/capricorn.png';
                case 'Aquarius':
                    return '/images/aquarius.png';
                case 'Pisces':
                    return '/images/pisces.png';
                default:
                    return '';
            }
        };

    const handleInputChange = (field: keyof FormData, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        setIsLoading(true);

        // Validation
        if (!formData.birthdate) {
            setError('Bitte gib dein Geburtsdatum ein.');
            setIsLoading(false);
            return;
        }

        if (!formData.birthtime) {
            setError('Bitte gib deine Geburtszeit ein.');
            setIsLoading(false);
            return;
        }

        if (!formData.birthlocation) {
            setError('Bitte wähle deinen Geburtsort aus.');
            setIsLoading(false);
            return;
        }

        try {
            const birthlocation_obj = COUNTIES.find((element) => element.id === formData.birthlocation);

            if (!birthlocation_obj) {
                setError('Ungültiger Geburtsort.');
                setIsLoading(false);
                return;
            }

            const year = formData.birthdate.slice(0, 4);
            const month = formData.birthdate.slice(5, 7);
            const day = formData.birthdate.slice(8, 10);
            const hour = formData.birthtime.slice(0, 2);
            const minute = formData.birthtime.slice(3, 5) || '00';
            const latitude = birthlocation_obj.latitude;
            const longitude = birthlocation_obj.longitude;

            if (!latitude || !longitude) {
                setError('Koordinaten für den Geburtsort nicht verfügbar.');
                setIsLoading(false);
                return;
            }

            // circular-natal-horoscope-js expects month 0–11 (like Date); <input type="date"> uses 1–12.
            const calculatedAscendant = calculateAscendant(
                parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10),
                parseInt(hour, 10),
                parseInt(minute, 10),
                latitude,
                longitude
            );

            if (!calculatedAscendant || !calculatedAscendant._ascendant || !calculatedAscendant._ascendant.Sign) {
                setError('Fehler bei der Berechnung. Bitte überprüfe deine Eingaben.');
                setIsLoading(false);
                return;
            }

            const ascendantLabel = calculatedAscendant._ascendant.Sign.label;
            setAscendant(ascendantLabel);
            setAscendantImage(getImage(ascendantLabel));
            
            // Save to history
            saveToHistory({
                birthdate: formData.birthdate,
                birthtime: formData.birthtime,
                birthlocation: formData.birthlocation,
                ascendant: ascendantLabel
            });
            setHistoryRefresh(prev => prev + 1);
        } catch (err) {
            setError('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
            console.error('Error calculating ascendant:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="row justify-content-center">
            <div className="col-12 col-lg-8">
                <h1 className="text-center mb-4">Aszendent Berechnen</h1>
                <form onSubmit={handleSubmit} className="mb-4">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <div className="mb-3">
                                <label htmlFor="birthdate" className="form-label">Dein Geburtstag</label>
                                <input 
                                    type="date" 
                                    className={`form-control ${error && !formData.birthdate ? 'is-invalid' : ''}`}
                                    id="birthdate" 
                                    value={formData.birthdate}
                                    onChange={(e) => handleInputChange('birthdate', e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="birthtime" className="form-label">Deine Geburtszeit</label>
                                <input 
                                    type="time" 
                                    className={`form-control ${error && !formData.birthtime ? 'is-invalid' : ''}`}
                                    id="birthtime" 
                                    value={formData.birthtime}
                                    onChange={(e) => handleInputChange('birthtime', e.target.value)}
                                    required
                                />
                                <small className="form-text text-muted">Die genaue Geburtszeit ist wichtig, da sich der Aszendent alle zwei Stunden ändert.</small>
                            </div>
                            <div className="mb-3">
                                <label htmlFor="birthlocation" className="form-label">Dein Geburtsort</label>
                                <select 
                                    className={`form-control ${error && !formData.birthlocation ? 'is-invalid' : ''}`}
                                    id="birthlocation" 
                                    value={formData.birthlocation}
                                    onChange={(e) => handleInputChange('birthlocation', parseInt(e.target.value))}
                                    required
                                >
                                    {COUNTIES.map((county) => (
                                        <option key={county.id} value={county.id}>{county.name}</option>
                                    ))}
                                </select>
                            </div>
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}
                            <button 
                                type="submit" 
                                className="btn btn-primary w-100 mt-3"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Berechne...
                                    </>
                                ) : (
                                    'Aszendent Berechnen'
                                )}
                            </button>
                        </div>
                    </div>
                </form>
                <AscendantResult title={ascendant} image={ascendantImage} />
                {ascendant && <SocialShare ascendant={ascendant} />}
                <History 
                    onSelectEntry={(entry) => {
                        setFormData({
                            birthdate: entry.birthdate,
                            birthtime: entry.birthtime,
                            birthlocation: entry.birthlocation
                        });
                        setAscendant(entry.ascendant);
                        setAscendantImage(getImage(entry.ascendant));
                    }}
                    refreshTrigger={historyRefresh}
                />
            </div>
        </div>
    );
}

export default AscendantForm;